import { createAsyncThunk } from '@reduxjs/toolkit';
import camelcaseKeys from 'camelcase-keys';

import { User } from '../features/authentication/authentication';
import { checkEventRegistrationStatus } from '../features/authentication/authentication-actions';
import { EventDetailsResponse } from '../features/event-details/event-detail-types';
import {
  EventRegistrationStatus,
  EventRegistrationStatusTypes,
} from '../features/purchase-tickets/purchase-actions';
import { getUserAsync, postUserSso } from '../utils/api';
import { endpoints } from '../utils/endpoints';
import { fetchJSON, ResponseError } from '../utils/fetch';
import { getSlugFromURL } from '../utils/get-slug';
import { logError } from '../utils/logging';
import { RootState } from './root-reducer';

export type EventDetailsParams = {
  slug: string;
  widgetId: string | null;
  code: string | null;
  widgetSource: string;
  token?: string;
  seriesExternalId: string | null;
};

type PublicEventSettings = {
  auth_enabled: boolean;
  features: {
    registrations_enable_quick_checkout: boolean;
    show_event_series: boolean;
  };
};

export type LoadWidgetResult = {
  eventDetails: EventDetailsResponse;
  widgetSource: string;
  registrationStatus: EventRegistrationStatusTypes;
  isExternalWidget: boolean;
  features: {
    registrations_enable_quick_checkout: boolean;
    show_event_series: boolean;
  };
} & FetchUserResponse;

const get = endpoints();

export const loadWidgetThunkAction = createAsyncThunk<
  LoadWidgetResult,
  EventDetailsParams,
  { state: RootState }
>(
  'widget/loadWidgetData',
  async ({ slug, widgetId, code, widgetSource }: EventDetailsParams) => {
    const eventSettings = await fetchEventSettings({ slug });

    const authEnabled = eventSettings.authEnabled;
    let registrationStatus: EventRegistrationStatusTypes =
      EventRegistrationStatus.NOT_REGISTERED;
    let userResponse: FetchUserResponse = { token: null, user: null };

    if (authEnabled) {
      userResponse = await fetchUser(slug);
    }

    const eventDetails = await fetchEventDetails({
      slug,
      widgetId,
      code,
      widgetSource,
      token: userResponse.token || undefined,
    });

    // TODO: Handle preview param https://hopin.atlassian.net/browse/HL-1210 and https://hopin.atlassian.net/browse/HL-1209
    if (userResponse.token) {
      const { registered: isRegistered, blocked: isBlocked } =
        await checkEventRegistrationStatus(
          slug,
          userResponse.user.email,
          userResponse.token,
        );

      registrationStatus = isBlocked
        ? EventRegistrationStatus.BLOCKED
        : isRegistered
        ? EventRegistrationStatus.REGISTERED
        : EventRegistrationStatus.NOT_REGISTERED;
    }

    return {
      ...userResponse,
      eventDetails,
      widgetId: eventDetails.widgetId,
      widgetSource,
      registrationStatus,
      isExternalWidget: Boolean(widgetId),
      features: {
        registrations_enable_quick_checkout:
          eventSettings.features?.registrations_enable_quick_checkout ?? false,
        show_event_series: eventSettings.features?.show_event_series ?? false,
      },
    };
  },
);

type FetchUserResponse =
  | {
      token: null;
      user: null;
    }
  | {
      token: string;
      user: User;
    };

/**
 * TODO: [HL-1482] this duplicates a lot of the logic withing authenticateUserAsync we should consolidate the code.
 * LoadWidgetThunk could be refactored to use this flow simplfying the number of occurences of these calls in isolation.
 */
const fetchUser = async (eventSlug: string): Promise<FetchUserResponse> => {
  try {
    const userToken = await postUserSso(eventSlug);

    if (userToken.token) {
      const user = await getUserAsync(userToken.token);

      return {
        token: userToken.token,
        user,
      };
    }

    return {
      token: null,
      user: null,
    };
  } catch (err) {
    const responseError = err as ResponseError;

    if (responseError?.status !== 401) logError(err as any);

    return {
      token: null,
      user: null,
    };
  }
};

const fetchEventSettings = async ({ slug }: { slug: string }) => {
  const eventSettingsEndpoint = get('EVENT_SETTINGS')(slug);
  const headers = { Accept: 'application/json' };
  try {
    const settingsResponse: PublicEventSettings = await fetchJSON(
      eventSettingsEndpoint,
      {
        headers,
      },
    );

    return {
      authEnabled: settingsResponse.auth_enabled ?? false,
      features: settingsResponse.features,
    };
  } catch (e) {
    return {
      authEnabled: false,
      features: {
        registrations_enable_quick_checkout: false,
        show_event_series: false,
      },
    };
  }
};

const ONSITE_WIDGET_ID = 'onsite';

export const fetchEventDetails = async ({
  widgetId,
  code,
  slug,
  widgetSource,
  token,
}: Omit<
  EventDetailsParams,
  'seriesExternalId'
>): Promise<EventDetailsResponse> => {
  let eventDetailsEndpoint = get('EVENT_DETAILS')(slug);

  if (widgetId == ONSITE_WIDGET_ID) {
    eventDetailsEndpoint = get('EVENT_DETAILS_ONSITE_WIDGET')(slug);
  } else if (widgetId) {
    eventDetailsEndpoint = get('EVENT_DETAILS_WIDGET')(slug, widgetId);
  }

  if (code) {
    eventDetailsEndpoint = eventDetailsEndpoint + `?code=${code}`;
  }

  const headers = token
    ? {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      }
    : { Accept: 'application/json' };

  const eventResponse = await fetchJSON(eventDetailsEndpoint, { headers });

  const {
    event,
    images,
    settings,
    tickets,
    forms,
    stripe_direct,
    ga_analytics_code,
    external_id,
    single_ticket_without_segments,
    tracking_integrations,
  } = eventResponse;

  return {
    event: {
      ...camelcaseKeys(event),
      url: getSlugFromURL(event.url),
      fullUrl: event.url,
    },
    images: camelcaseKeys(images),
    settings: camelcaseKeys(settings),
    tickets: camelcaseKeys(tickets),
    forms: camelcaseKeys(forms),
    stripeDirect: stripe_direct,
    googleAnalyticsCode: ga_analytics_code,
    widgetId: external_id || widgetId,
    widgetSource,
    single_ticket_without_segments,
    trackingIntegrations: tracking_integrations?.data || [],
  };
};
